import React, { useEffect, useMemo, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import {
  getAvailabilityByDate,
  updateSingleSlot,
  deleteSingleSlot,
  deleteEntireDay,
  deleteSession,
  deleteRound,
  deleteOperation,
  clearMessages,
  addEntireDay,
  addSingleSlot,
  updateRound, // ADD
  updateOperation,
} from "../../redux/apis/Availability/doctorAvailabilitySlice";

import { getBusinessUserProfileImageUrl } from "../utils/buserimageUtils";

// ============================================================
// CONSTANTS
// ============================================================

const CUSTOM_GRADIENT =
  "linear-gradient(145deg, rgb(15, 31, 51) 0%, rgb(26, 58, 92) 50%, rgb(30, 73, 118) 100%)";

const BUTTON_GRADIENT =
  "linear-gradient(135deg, rgb(26, 58, 92), rgb(14, 165, 233))";

const SESSION_RANGES = {
  Morning: {
    start: 360,
    end: 720,
  },
  Afternoon: {
    start: 720,
    end: 900,
  },
  Evening: {
    start: 900,
    end: 1140,
  },
  Night: {
    start: 1140,
    end: 1320,
  },
};

const DEFAULT_SESSIONS = [
  {
    name: "Morning",
    start: "06:00",
    end: "12:00",
  },
  {
    name: "Afternoon",
    start: "12:00",
    end: "15:00",
  },
  {
    name: "Evening",
    start: "15:00",
    end: "19:00",
  },
  {
    name: "Night",
    start: "19:00",
    end: "22:00",
  },
];

// ============================================================
// DATE HELPERS
// ============================================================

const getTodayString = () => {
  const now = new Date();

  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(now.getDate()).padStart(2, "0")}`;
};

const getMaxDateString = () => {
  const date = new Date();

  date.setDate(date.getDate() + 7);

  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(date.getDate()).padStart(2, "0")}`;
};

const isPastDate = (date) => {
  return date < getTodayString();
};

const isTodayDate = (date) => {
  return date === getTodayString();
};

const formatDisplayDate = (dateString) => {
  if (!dateString) return "";

  const [year, month, day] = dateString.split("-").map(Number);

  const date = new Date(year, month - 1, day);

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getWeekDays = () => {
  const today = new Date();

  return Array.from({ length: 8 }, (_, i) => {
    const date = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() + i,
    );

    return {
      date: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
        2,
        "0",
      )}-${String(date.getDate()).padStart(2, "0")}`,

      day: date.toLocaleDateString("en-IN", {
        weekday: "short",
      }),

      fullDay: date.toLocaleDateString("en-IN", {
        weekday: "long",
      }),
    };
  });
};

// ============================================================
// TIME HELPERS
// ============================================================

const timeToMinutes = (time = "") => {
  if (!time || typeof time !== "string" || !time.includes(":")) {
    return null;
  }

  const [hours, minutes] = time.split(":").map(Number);

  if (
    Number.isNaN(hours) ||
    Number.isNaN(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null;
  }

  return hours * 60 + minutes;
};

const minutesToTime = (minutes) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;

  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const formatTime12 = (time) => {
  const minutes = timeToMinutes(time);

  if (minutes === null) {
    return "--:--";
  }

  let hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  const suffix = hours >= 12 ? "PM" : "AM";

  hours = hours % 12;

  if (hours === 0) {
    hours = 12;
  }

  return `${hours}:${String(mins).padStart(2, "0")} ${suffix}`;
};

const getDurationMinutes = (start, end) => {
  const s = timeToMinutes(start);
  const e = timeToMinutes(end);

  if (s === null || e === null || e <= s) {
    return 0;
  }

  return e - s;
};

const formatDuration = (start, end) => {
  const minutes = getDurationMinutes(start, end);

  if (!minutes) return "";

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours && mins) {
    return `${hours} hr ${mins} min`;
  }

  if (hours) {
    return `${hours} hr`;
  }

  return `${mins} min`;
};

// ============================================================
// ID HELPERS
// ============================================================

const getSlotId = (slot) => {
  if (!slot) return null;

  return slot.slotId || slot._id || slot.id || null;
};

const getRoundId = (round) => {
  if (!round) return null;

  return round.roundId || round._id || round.id || null;
};

const getOperationId = (operation) => {
  if (!operation) return null;

  return operation.operationId || operation._id || operation.id || null;
};

// ============================================================
// OVERLAP
// ============================================================

const timesOverlap = (startA, endA, startB, endB) => {
  const aStart = timeToMinutes(startA);
  const aEnd = timeToMinutes(endA);
  const bStart = timeToMinutes(startB);
  const bEnd = timeToMinutes(endB);

  if (aStart === null || aEnd === null || bStart === null || bEnd === null) {
    return false;
  }

  return aStart < bEnd && bStart < aEnd;
};

// ============================================================
// PAST TIME
// ============================================================

const isPastTimeToday = (time) => {
  const minutes = timeToMinutes(time);

  if (minutes === null) {
    return false;
  }

  const now = new Date();

  const currentMinutes = now.getHours() * 60 + now.getMinutes();

  return minutes <= currentMinutes;
};

// ============================================================
// NORMALIZERS
// ============================================================

const normalizeRound = (round = {}) => ({
  ...round,

  name: round.name || round.roundName || "Doctor Round",

  startTime: round.startTime || round.start || "",

  endTime: round.endTime || round.end || "",

  enabled: round.enabled !== false,

  isActive: round.isActive !== false,
});

const normalizeOperation = (operation = {}) => ({
  ...operation,

  name:
    operation.name ||
    operation.operationName ||
    operation.procedureName ||
    "Operation",

  startTime: operation.startTime || operation.start || "",

  endTime: operation.endTime || operation.end || "",

  enabled: operation.enabled !== false,

  isActive: operation.isActive !== false,
});

const normalizeSession = (session = {}) => ({
  ...session,

  name: session.name || session.sessionName || "Session",

  slots: Array.isArray(session.slots) ? session.slots : [],
});

// ============================================================
// COMPONENT
// ============================================================

const DoctorSlotManager = () => {
  const dispatch = useDispatch();

  // ==========================================================
  // USER
  // ==========================================================

  const reduxUser = useSelector((state) => state.auth.user);

  // ==========================================================
  // REDUX
  // ==========================================================

  const {
    sessions = [],
    doctorOnRounds = [],
    operationSchedule = [],
    isHoliday = false,
    holidayReason = "",
    loading,
    error,
    success,
  } = useSelector((state) => state.doctorAvailability || {});

  // ==========================================================
  // DATE
  // ==========================================================

  const weekDays = useMemo(() => getWeekDays(), []);

  const [selectedDate, setSelectedDate] = useState(getTodayString());

  // ==========================================================
  // UI STATE
  // ==========================================================

  const [editingSlotId, setEditingSlotId] = useState(null);

  const [editValues, setEditValues] = useState({
    startTime: "",
    endTime: "",
  });

  const [showAddSlot, setShowAddSlot] = useState(false);

  const [showFreeOnly, setShowFreeOnly] = useState(false);

  const [addingDay, setAddingDay] = useState(false);

  const [addingSlot, setAddingSlot] = useState(false);

  const [deletingAction, setDeletingAction] = useState(null);

  const [newSlot, setNewSlot] = useState({
    sessionName: "Morning",
    startTime: "",
    endTime: "",
  });

  // ==========================================================
  // DATE STATUS
  // ==========================================================

  const selectedDateIsPast = isPastDate(selectedDate);

  const selectedDateIsToday = isTodayDate(selectedDate);

  const canModifyDate = !selectedDateIsPast && !isHoliday;

  // ==========================================================
  // SAFE SESSIONS
  // ==========================================================

  const safeSessions = useMemo(() => {
    if (!Array.isArray(sessions)) {
      return [];
    }

    return sessions.map(normalizeSession);
  }, [sessions]);

  // ==========================================================
  // NORMALIZED ROUNDS
  // ==========================================================

  const normalizedRounds = useMemo(() => {
    if (!Array.isArray(doctorOnRounds)) {
      return [];
    }

    return doctorOnRounds
      .filter((round) => round?.enabled !== false && round?.isActive !== false)
      .map(normalizeRound)
      .filter(
        (round) =>
          timeToMinutes(round.startTime) !== null &&
          timeToMinutes(round.endTime) !== null,
      );
  }, [doctorOnRounds]);

  // ==========================================================
  // NORMALIZED OPERATIONS
  // ==========================================================

  const normalizedOperations = useMemo(() => {
    if (!Array.isArray(operationSchedule)) {
      return [];
    }

    return operationSchedule
      .filter(
        (operation) =>
          operation?.enabled !== false && operation?.isActive !== false,
      )
      .map(normalizeOperation)
      .filter(
        (operation) =>
          timeToMinutes(operation.startTime) !== null &&
          timeToMinutes(operation.endTime) !== null,
      );
  }, [operationSchedule]);

  // ==========================================================
  // SESSION VALIDATION
  // ==========================================================

  const validateSessionTime = (sessionName, startTime, endTime) => {
    const range = SESSION_RANGES[sessionName];

    if (!range) {
      return {
        valid: false,
        message: "Invalid session selected.",
      };
    }

    const start = timeToMinutes(startTime);

    const end = timeToMinutes(endTime);

    if (start === null || end === null) {
      return {
        valid: false,
        message: "Please enter valid start and end times.",
      };
    }

    if (end <= start) {
      return {
        valid: false,
        message: "End time must be after start time.",
      };
    }

    if (start < range.start || end > range.end) {
      return {
        valid: false,
        message:
          `${sessionName} session allows only ` +
          `${minutesToTime(range.start)} - ${minutesToTime(range.end)}.`,
      };
    }

    return {
      valid: true,
    };
  };

  // ==========================================================
  // ROUND OVERLAP
  // ==========================================================

  const findRoundOverlap = (startTime, endTime) => {
    return (
      normalizedRounds.find((round) =>
        timesOverlap(startTime, endTime, round.startTime, round.endTime),
      ) || null
    );
  };

  // ==========================================================
  // OPERATION OVERLAP
  // ==========================================================

  const findOperationOverlap = (startTime, endTime) => {
    return (
      normalizedOperations.find((operation) =>
        timesOverlap(
          startTime,
          endTime,
          operation.startTime,
          operation.endTime,
        ),
      ) || null
    );
  };

  // ==========================================================
  // BLOCKING SCHEDULE
  // ROUND FIRST
  // OPERATION SECOND
  // ==========================================================

  const findBlockingSchedule = (startTime, endTime) => {
    const round = findRoundOverlap(startTime, endTime);

    if (round) {
      return {
        type: "round",
        name: round.name,
        startTime: round.startTime,
        endTime: round.endTime,
      };
    }

    const operation = findOperationOverlap(startTime, endTime);

    if (operation) {
      return {
        type: "operation",
        name: operation.name,
        startTime: operation.startTime,
        endTime: operation.endTime,
      };
    }

    return null;
  };

  // ==========================================================
  // PATIENT SLOT OVERLAP
  // ==========================================================

  const findPatientSlotOverlap = (startTime, endTime, excludeSlotId = null) => {
    for (const session of safeSessions) {
      for (const slot of session.slots || []) {
        const currentSlotId = getSlotId(slot);

        if (excludeSlotId && currentSlotId === excludeSlotId) {
          continue;
        }

        if (slot.isActive === false) {
          continue;
        }

        if (timesOverlap(startTime, endTime, slot.startTime, slot.endTime)) {
          return {
            ...slot,
            sessionName: session.name,
          };
        }
      }
    }

    return null;
  };

  // ==========================================================
  // PATIENT TIME VALIDATION
  // ==========================================================

  const validatePatientTime = (
    sessionName,
    startTime,
    endTime,
    excludeSlotId = null,
  ) => {
    const sessionValidation = validateSessionTime(
      sessionName,
      startTime,
      endTime,
    );

    if (!sessionValidation.valid) {
      return sessionValidation;
    }

    // ROUND FIRST
    const round = findRoundOverlap(startTime, endTime);

    if (round) {
      return {
        valid: false,
        type: "round",
        blocker: round,
        message: `This patient slot overlaps Doctor Round "${round.name}" (${formatTime12(
          round.startTime,
        )} - ${formatTime12(round.endTime)}).`,
      };
    }

    // OPERATION SECOND
    const operation = findOperationOverlap(startTime, endTime);

    if (operation) {
      return {
        valid: false,
        type: "operation",
        blocker: operation,
        message: `This patient slot overlaps Operation "${operation.name}" (${formatTime12(
          operation.startTime,
        )} - ${formatTime12(operation.endTime)}).`,
      };
    }

    // PATIENT SLOT THIRD
    const patientSlot = findPatientSlotOverlap(
      startTime,
      endTime,
      excludeSlotId,
    );

    if (patientSlot) {
      return {
        valid: false,
        type: "patient",
        blocker: patientSlot,
        message: `This patient slot overlaps another patient slot (${formatTime12(
          patientSlot.startTime,
        )} - ${formatTime12(patientSlot.endTime)}).`,
      };
    }

    return {
      valid: true,
    };
  };

  // ==========================================================
  // SLOT PAST
  // ==========================================================

  const isSlotPast = (slot) => {
    if (selectedDateIsPast) {
      return true;
    }

    if (!selectedDateIsToday) {
      return false;
    }

    return isPastTimeToday(slot?.startTime);
  };

  // ==========================================================
  // BOOKABLE SLOT
  // ==========================================================

  const isSlotBookable = (slot) => {
    if (!slot) return false;

    if (slot.isActive === false) {
      return false;
    }

    if (slot.isBooked) {
      return false;
    }

    if (isSlotPast(slot)) {
      return false;
    }

    const blocker = findBlockingSchedule(slot.startTime, slot.endTime);

    if (blocker) {
      return false;
    }

    return true;
  };

  // ==========================================================
  // LOAD DATE
  // ==========================================================

  useEffect(() => {
    if (!reduxUser?._id) return;

    dispatch(
      getAvailabilityByDate({
        doctorId: reduxUser._id,
        date: selectedDate,
      }),
    );
  }, [reduxUser?._id, selectedDate, dispatch]);

  // ==========================================================
  // CLEAR MESSAGES
  // ==========================================================

  useEffect(() => {
    if (!error && !success) return;

    const timer = setTimeout(() => {
      dispatch(clearMessages());
    }, 3000);

    return () => clearTimeout(timer);
  }, [error, success, dispatch]);

  // ==========================================================
  // REFRESH
  // ==========================================================

  const refreshAvailability = async () => {
    if (!reduxUser?._id) return;

    await dispatch(
      getAvailabilityByDate({
        doctorId: reduxUser._id,
        date: selectedDate,
      }),
    );
  };

  // ==========================================================
  // SELECT DATE
  // ==========================================================

  const selectDate = (date) => {
    if (!date) return;

    setEditingSlotId(null);

    setEditValues({
      startTime: "",
      endTime: "",
    });

    setShowAddSlot(false);

    setSelectedDate(date);
  };

  // ==========================================================
  // CHANGE DAY
  // ==========================================================

  const changeDay = (days) => {
    const [year, month, day] = selectedDate.split("-").map(Number);

    const date = new Date(year, month - 1, day);

    date.setDate(date.getDate() + days);

    const newDate = `${date.getFullYear()}-${String(
      date.getMonth() + 1,
    ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

    if (newDate < getTodayString() || newDate > getMaxDateString()) {
      return;
    }

    selectDate(newDate);
  };

  // ==========================================================
  // ADD SLOT
  // ==========================================================

  const handleAddSlot = async () => {
    if (!canModifyDate) {
      alert(
        selectedDateIsPast
          ? "Past dates are view-only."
          : "Holiday dates cannot be modified.",
      );
      return;
    }

    if (addingSlot) return;

    if (!newSlot.startTime || !newSlot.endTime) {
      alert("Please select start and end time.");
      return;
    }

    const validation = validatePatientTime(
      newSlot.sessionName,
      newSlot.startTime,
      newSlot.endTime,
    );

    if (!validation.valid) {
      alert(validation.message);
      return;
    }

    if (selectedDateIsToday && isPastTimeToday(newSlot.startTime)) {
      alert("You cannot add a slot whose start time has already passed.");
      return;
    }

    if (!reduxUser?._id) return;

    setAddingSlot(true);

    try {
      const result = await dispatch(
        addSingleSlot({
          doctorId: reduxUser._id,

          date: selectedDate,

          session: newSlot.sessionName,

          sessionName: newSlot.sessionName,

          startTime: newSlot.startTime,

          endTime: newSlot.endTime,

          isBooked: false,
        }),
      );

      if (result?.meta?.requestStatus === "fulfilled") {
        setShowAddSlot(false);

        setNewSlot({
          sessionName: "Morning",
          startTime: "",
          endTime: "",
        });

        await refreshAvailability();
      }
    } finally {
      setAddingSlot(false);
    }
  };

  // ==========================================================
  // GENERATE SLOTS
  // ==========================================================

  const generateAvailableSlots = (sessionName, start, end, duration = 30) => {
    const slots = [];

    const startMinutes = timeToMinutes(start);

    const endMinutes = timeToMinutes(end);

    if (startMinutes === null || endMinutes === null) {
      return slots;
    }

    let current = startMinutes;

    const now = new Date();

    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    while (current + duration <= endMinutes) {
      const next = current + duration;

      const slotStart = minutesToTime(current);

      const slotEnd = minutesToTime(next);

      // NEVER CREATE PAST SLOTS FOR TODAY
      if (selectedDateIsToday && current <= currentMinutes) {
        current = next;
        continue;
      }

      const validation = validatePatientTime(sessionName, slotStart, slotEnd);

      if (validation.valid) {
        slots.push({
          startTime: slotStart,
          endTime: slotEnd,
          isBooked: false,
          isActive: true,
        });
      }

      current = next;
    }

    return slots;
  };

  // ==========================================================
  // ADD ENTIRE DAY
  // ==========================================================

  const handleAddDaySlots = async () => {
    if (!canModifyDate) {
      alert(
        selectedDateIsPast
          ? "Past dates cannot be overwritten."
          : "Holiday dates cannot be modified.",
      );
      return;
    }

    if (addingDay || !reduxUser?._id) {
      return;
    }

    setAddingDay(true);

    try {
      const payload = {
        doctorId: reduxUser._id,

        date: selectedDate,

        isHoliday: false,

        sessions: DEFAULT_SESSIONS.map((session) => ({
          name: session.name,

          slots: generateAvailableSlots(
            session.name,
            session.start,
            session.end,
            30,
          ),
        })),
      };

      const result = await dispatch(addEntireDay(payload));

      if (result?.meta?.requestStatus === "fulfilled") {
        await refreshAvailability();
      }
    } finally {
      setAddingDay(false);
    }
  };

  // ==========================================================
  // UPDATE SLOT
  // ==========================================================

  const handleUpdateSlot = async (slot) => {
    if (!canModifyDate) {
      alert(
        selectedDateIsPast
          ? "Past dates cannot be overwritten."
          : "Holiday dates cannot be modified.",
      );
      return;
    }

    if (isSlotPast(slot)) {
      alert("This past slot cannot be modified.");
      return;
    }

    if (slot.isBooked) {
      alert("Booked slots cannot be modified.");
      return;
    }

    const slotId = getSlotId(slot);

    if (!slotId) {
      alert("Slot ID is missing.");
      return;
    }

    const sessionName =
      slot.sessionName ||
      slot.session ||
      safeSessions.find((session) =>
        session.slots?.some((item) => getSlotId(item) === slotId),
      )?.name;

    const validation = validatePatientTime(
      sessionName,
      editValues.startTime,
      editValues.endTime,
      slotId,
    );

    if (!validation.valid) {
      alert(validation.message);
      return;
    }

    if (selectedDateIsToday && isPastTimeToday(editValues.startTime)) {
      alert("You cannot move a slot into a time that has already passed.");
      return;
    }

    const result = await dispatch(
      updateSingleSlot({
        slotId,
        date: selectedDate,
        startTime: editValues.startTime,
        endTime: editValues.endTime,
      }),
    );

    if (result?.meta?.requestStatus === "fulfilled") {
      setEditingSlotId(null);

      setEditValues({
        startTime: "",
        endTime: "",
      });

      await refreshAvailability();
    }
  };

  // ==========================================================
  // DELETE SLOT
  // ==========================================================

  const handleDeleteSlot = async (slot) => {
    if (!canModifyDate) {
      alert(
        selectedDateIsPast
          ? "Past dates cannot be changed."
          : "Holiday dates cannot be modified.",
      );
      return;
    }

    if (isSlotPast(slot)) {
      alert("Past slots cannot be deleted.");
      return;
    }

    if (slot.isBooked) {
      alert("Booked slots cannot be deleted.");
      return;
    }

    const slotId = getSlotId(slot);

    if (!slotId) {
      alert("Slot ID is missing.");
      return;
    }

    if (
      !window.confirm(
        `Delete ${formatTime12(slot.startTime)} - ${formatTime12(
          slot.endTime,
        )}?`,
      )
    ) {
      return;
    }

    setDeletingAction(`slot-${slotId}`);

    try {
      const result = await dispatch(
        deleteSingleSlot({
          slotId,
          date: selectedDate,
        }),
      );

      if (result?.meta?.requestStatus === "fulfilled") {
        await refreshAvailability();
      }
    } finally {
      setDeletingAction(null);
    }
  };

  // ==========================================================
  // DELETE SESSION
  // ==========================================================

  const handleDeleteSession = async (session) => {
    if (!canModifyDate) {
      alert(
        selectedDateIsPast
          ? "Past dates cannot be changed."
          : "Holiday dates cannot be modified.",
      );
      return;
    }

    if (!session?.name) {
      alert("Session name is missing.");
      return;
    }

    const bookedSlots = (session.slots || []).filter((slot) => slot.isBooked);

    if (bookedSlots.length > 0) {
      alert("This session contains booked appointments and cannot be deleted.");
      return;
    }

    if (
      !window.confirm(
        `Delete the entire ${session.name} session for ${formatDisplayDate(
          selectedDate,
        )}?`,
      )
    ) {
      return;
    }

    const key = `session-${session.name}`;

    setDeletingAction(key);

    try {
      const result = await dispatch(
        deleteSession({
          date: selectedDate,
          sessionName: session.name,
        }),
      );

      if (result?.meta?.requestStatus === "fulfilled") {
        await refreshAvailability();
      }
    } finally {
      setDeletingAction(null);
    }
  };

  // ==========================================================
  // DELETE ROUND
  // ==========================================================

  const handleDeleteRound = async (round) => {
    if (!canModifyDate) {
      alert(
        selectedDateIsPast
          ? "Past dates cannot be changed."
          : "Holiday dates cannot be modified.",
      );
      return;
    }

    const roundId = getRoundId(round);

    if (!roundId) {
      alert("Round ID is missing.");
      return;
    }

    const bookedOverlap = safeSessions.some((session) =>
      (session.slots || []).some(
        (slot) =>
          slot.isBooked &&
          timesOverlap(
            slot.startTime,
            slot.endTime,
            round.startTime,
            round.endTime,
          ),
      ),
    );

    if (bookedOverlap) {
      alert(
        "This round overlaps a booked patient appointment and cannot be deleted.",
      );
      return;
    }

    if (
      !window.confirm(
        `Delete Doctor Round "${round.name}" (${formatTime12(
          round.startTime,
        )} - ${formatTime12(round.endTime)})?`,
      )
    ) {
      return;
    }

    const key = `round-${roundId}`;

    setDeletingAction(key);

    try {
      const result = await dispatch(
        deleteRound({
          date: selectedDate,
          roundId,
        }),
      );

      if (result?.meta?.requestStatus === "fulfilled") {
        await refreshAvailability();
      }
    } finally {
      setDeletingAction(null);
    }
  };

  // ==========================================================
  // DELETE OPERATION
  // ==========================================================

  const handleDeleteOperation = async (operation) => {
    if (!canModifyDate) {
      alert(
        selectedDateIsPast
          ? "Past dates cannot be changed."
          : "Holiday dates cannot be modified.",
      );
      return;
    }

    const operationId = getOperationId(operation);

    if (!operationId) {
      alert("Operation ID is missing.");
      return;
    }

    const bookedOverlap = safeSessions.some((session) =>
      (session.slots || []).some(
        (slot) =>
          slot.isBooked &&
          timesOverlap(
            slot.startTime,
            slot.endTime,
            operation.startTime,
            operation.endTime,
          ),
      ),
    );

    if (bookedOverlap) {
      alert(
        "This operation overlaps a booked patient appointment and cannot be deleted.",
      );
      return;
    }

    if (
      !window.confirm(
        `Delete Operation "${operation.name}" (${formatTime12(
          operation.startTime,
        )} - ${formatTime12(operation.endTime)})?`,
      )
    ) {
      return;
    }

    const key = `operation-${operationId}`;

    setDeletingAction(key);

    try {
      const result = await dispatch(
        deleteOperation({
          date: selectedDate,
          operationId,
        }),
      );

      if (result?.meta?.requestStatus === "fulfilled") {
        await refreshAvailability();
      }
    } finally {
      setDeletingAction(null);
    }
  };

  // ==========================================================
  // DELETE ENTIRE DAY
  // ==========================================================

  const handleDeleteDay = async () => {
    if (!canModifyDate) {
      alert(
        selectedDateIsPast
          ? "Past dates cannot be deleted."
          : "Holiday dates cannot be deleted.",
      );
      return;
    }

    if (!reduxUser?._id) return;

    const hasBooked = safeSessions.some((session) =>
      (session.slots || []).some((slot) => slot.isBooked),
    );

    if (hasBooked) {
      alert(
        "This day contains booked appointments. Delete the booked appointments first.",
      );
      return;
    }

    if (
      !window.confirm(
        `Delete availability for ${formatDisplayDate(selectedDate)}?`,
      )
    ) {
      return;
    }

    setDeletingAction("day");

    try {
      const result = await dispatch(
        deleteEntireDay({
          doctorId: reduxUser._id,
          date: selectedDate,
        }),
      );

      if (result?.meta?.requestStatus === "fulfilled") {
        await refreshAvailability();
      }
    } finally {
      setDeletingAction(null);
    }
  };

  // ==========================================================
  // DISPLAYED SESSIONS
  // ==========================================================

  const displayedSessions = useMemo(() => {
    if (!showFreeOnly) {
      return safeSessions;
    }

    return safeSessions
      .map((session) => ({
        ...session,

        slots: session.slots.filter(isSlotBookable),
      }))
      .filter((session) => session.slots.length > 0);
  }, [
    safeSessions,
    showFreeOnly,
    selectedDate,
    normalizedRounds,
    normalizedOperations,
  ]);

  // ==========================================================
  // STATS
  // ==========================================================

  const stats = useMemo(() => {
    let free = 0;
    let booked = 0;
    let blocked = 0;
    let past = 0;

    safeSessions.forEach((session) => {
      (session.slots || []).forEach((slot) => {
        if (slot.isActive === false) {
          return;
        }

        const slotPast = isSlotPast(slot);

        const blocker = findBlockingSchedule(slot.startTime, slot.endTime);

        if (slot.isBooked) {
          booked++;
        } else if (slotPast) {
          past++;
        } else if (blocker) {
          blocked++;
        } else {
          free++;
        }
      });
    });

    return {
      free,
      booked,
      blocked,
      past,
      total: free + booked + blocked + past,
    };
  }, [safeSessions, normalizedRounds, normalizedOperations, selectedDate]);

  // ==========================================================
  // LOGIN
  // ==========================================================

  if (!reduxUser) {
    return <div className="p-6 text-center">Please login</div>;
  }

  // ==========================================================
  // JSX
  // ==========================================================

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ======================================================
          TOP HEADER
      ====================================================== */}

      <header
        className="sticky top-0 z-50 shadow-lg"
        style={{
          background: CUSTOM_GRADIENT,
        }}
      >
        <div className="max-w-[1700px] mx-auto px-4 md:px-6 py-3">
          <div className="flex items-center justify-between gap-3">
            {/* LEFT */}

            <div className="flex items-center gap-3 min-w-0">
              <button
                type="button"
                onClick={() => window.history.back()}
                className="
                  flex items-center justify-center
                  w-9 h-9 md:w-10 md:h-10
                  rounded-lg
                  bg-white/10
                  hover:bg-white/20
                  border border-white/10
                  text-white
                  transition
                  shrink-0
                "
                title="Go Back"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>

              <div className="min-w-0">
                <h1 className="text-lg md:text-2xl font-bold text-white truncate">
                  Doctor Slot Management
                </h1>

                <p className="hidden sm:block text-xs md:text-sm text-slate-300 mt-0.5 truncate">
                  Manage doctor rounds, operation schedules and patient
                  appointment slots
                </p>
              </div>
            </div>

            {/* RIGHT */}

            <div className="flex items-center gap-2 md:gap-4 shrink-0">
              <div className="hidden sm:block text-right">
                <div className="text-[9px] md:text-[10px] uppercase tracking-wider text-slate-400">
                  Selected Date
                </div>

                <div className="font-bold text-white text-sm md:text-base">
                  {formatDisplayDate(selectedDate)}
                </div>
              </div>

              <div
                className="
                  flex items-center
                  gap-2 md:gap-3
                  pl-2 md:pl-3
                  border-l border-white/20
                "
              >
                <div className="relative shrink-0">
                  {reduxUser?.profileImage ? (
                    <img
                      src={getBusinessUserProfileImageUrl(
                        reduxUser.profileImage,
                      )}
                      alt={reduxUser?.name || reduxUser?.fullName || "Doctor"}
                      className="
                        w-10 h-10
                        md:w-12 md:h-12
                        rounded-full
                        object-cover
                        border-2 border-white/70
                        shadow-md
                        bg-white
                      "
                      onError={(e) => {
                        e.currentTarget.style.display = "none";

                        if (e.currentTarget.nextElementSibling) {
                          e.currentTarget.nextElementSibling.style.display =
                            "flex";
                        }
                      }}
                    />
                  ) : null}

                  <div
                    className={`
                      ${reduxUser?.profileImage ? "hidden" : "flex"}
                      w-10 h-10
                      md:w-12 md:h-12
                      rounded-full
                      bg-white/10
                      border-2 border-white/30
                      items-center
                      justify-center
                      text-lg md:text-xl
                    `}
                  >
                    🩺
                  </div>

                  <span
                    className="
                      absolute
                      bottom-0
                      right-0
                      w-3
                      h-3
                      rounded-full
                      bg-green-400
                      border-2
                      border-slate-900
                    "
                  />
                </div>

                <div className="hidden md:block max-w-[220px]">
                  <div className="text-[9px] uppercase tracking-wider text-slate-400">
                    Logged in as
                  </div>

                  <div className="text-sm font-bold text-white truncate">
                    {reduxUser?.fullName ||
                      reduxUser?.name ||
                      reduxUser?.doctorName ||
                      "Doctor"}
                  </div>

                  {reduxUser?.specialization && (
                    <div className="text-[11px] text-slate-300 truncate">
                      {reduxUser.specialization}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="sm:hidden mt-2 flex items-center justify-between">
            <div>
              <div className="text-[9px] uppercase tracking-wider text-slate-400">
                Selected Date
              </div>

              <div className="font-bold text-white text-xs">
                {formatDisplayDate(selectedDate)}
              </div>
            </div>

            <div className="text-[10px] text-slate-400">
              {reduxUser?.specialization || "Doctor"}
            </div>
          </div>
        </div>
      </header>

      {/* ======================================================
          MAIN
      ====================================================== */}

      <div className="max-w-[1700px] mx-auto p-4 md:p-6">
        <div className="grid grid-cols-1 lg:grid-cols-[230px_330px_minmax(0,1fr)] gap-5 items-start">
          {/* ==================================================
              COLUMN 1 — DAYS
          ================================================== */}

          <aside className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden lg:sticky lg:top-[100px]">
            <div
              className="px-4 py-4 text-white"
              style={{
                background: CUSTOM_GRADIENT,
              }}
            >
              <div className="font-bold">Schedule Days</div>

              <div className="text-xs text-slate-300 mt-1">Next 7 days</div>
            </div>

            <div className="p-3 space-y-2">
              {weekDays.map((day) => {
                const selected = selectedDate === day.date;

                const past = isPastDate(day.date);

                const today = isTodayDate(day.date);

                return (
                  <button
                    key={day.date}
                    onClick={() => selectDate(day.date)}
                    className={`w-full rounded-xl p-3 text-left border transition ${
                      selected
                        ? "text-white shadow-md border-transparent"
                        : "bg-white hover:bg-slate-50 border-slate-200"
                    }`}
                    style={
                      selected
                        ? {
                            background: BUTTON_GRADIENT,
                          }
                        : {}
                    }
                  >
                    <div className="flex justify-between items-center gap-2">
                      <div>
                        <div
                          className={`font-bold ${
                            selected ? "text-white" : "text-slate-800"
                          }`}
                        >
                          {day.fullDay}
                        </div>

                        <div
                          className={`text-xs mt-1 ${
                            selected ? "text-slate-200" : "text-slate-500"
                          }`}
                        >
                          {formatDisplayDate(day.date)}
                        </div>
                      </div>

                      <div>
                        {today && (
                          <span className="text-[9px] px-2 py-1 rounded-full bg-cyan-500 text-white font-bold">
                            TODAY
                          </span>
                        )}

                        {past && !today && (
                          <span
                            className={`text-[9px] px-2 py-1 rounded-full ${
                              selected
                                ? "bg-slate-500 text-white"
                                : "bg-slate-200 text-slate-600"
                            }`}
                          >
                            LOCKED
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}

              <div className="pt-4 mt-3 border-t border-slate-200">
                <label className="text-xs font-semibold text-slate-500">
                  Select Date
                </label>

                <input
                  type="date"
                  min={getTodayString()}
                  max={getMaxDateString()}
                  value={selectedDate}
                  onChange={(e) => selectDate(e.target.value)}
                  className="w-full mt-2 border border-slate-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>
          </aside>

          {/* ==================================================
              COLUMN 2
          ================================================== */}

          <div className="space-y-5">
            {/* DATE CONTROLS */}

            <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
              <div className="text-xs uppercase tracking-wide text-slate-400">
                Selected Schedule
              </div>

              <div className="font-bold text-lg text-slate-800 mt-1">
                {selectedDate}
              </div>

              <div className="text-xs text-slate-500 mt-1">
                {selectedDateIsPast
                  ? "Historical view"
                  : selectedDateIsToday
                    ? "Today — passed times locked"
                    : "Future availability"}
              </div>

              <div className="grid grid-cols-2 gap-2 mt-4">
                <button
                  onClick={() => changeDay(-1)}
                  disabled={selectedDate <= getTodayString()}
                  className="px-2 py-2 rounded-lg bg-slate-200 hover:bg-slate-300 text-xs font-semibold disabled:opacity-40"
                >
                  ← Previous
                </button>

                <button
                  onClick={() => changeDay(1)}
                  disabled={selectedDate >= getMaxDateString()}
                  className="px-2 py-2 rounded-lg bg-slate-200 hover:bg-slate-300 text-xs font-semibold disabled:opacity-40"
                >
                  Next →
                </button>
              </div>

              <button
                onClick={refreshAvailability}
                disabled={loading}
                className="w-full mt-2 px-3 py-2 rounded-lg text-white text-xs font-semibold disabled:opacity-50"
                style={{
                  background: BUTTON_GRADIENT,
                }}
              >
                ↻ Refresh
              </button>
            </section>

            {/* HOLIDAY */}

            {isHoliday && (
              <section className="rounded-2xl border border-orange-300 bg-orange-50 p-4">
                <div className="font-bold text-orange-800">🏖️ Holiday</div>

                {holidayReason && (
                  <div className="text-xs text-orange-700 mt-1">
                    {holidayReason}
                  </div>
                )}
              </section>
            )}

            {/* ROUND */}

            <section className="bg-white rounded-2xl border border-amber-200 shadow-sm overflow-hidden">
              <div className="px-4 py-4 bg-amber-50 border-b border-amber-200">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <h2 className="font-bold text-amber-900">
                      👨‍⚕️ Doctor On Rounds
                    </h2>

                    <p className="text-[11px] text-amber-700 mt-1">
                      Patient slots blocked
                    </p>
                  </div>

                  <span className="px-2 py-1 rounded-full bg-amber-200 text-amber-900 text-[10px] font-bold">
                    {normalizedRounds.length}
                  </span>
                </div>
              </div>

              <div className="p-3">
                {normalizedRounds.length === 0 ? (
                  <div className="text-center py-6 text-xs text-slate-400">
                    No doctor rounds
                  </div>
                ) : (
                  <div className="space-y-3">
                    {normalizedRounds.map((round, index) => {
                      const roundId = getRoundId(round);

                      const deleting = deletingAction === `round-${roundId}`;

                      return (
                        <div
                          key={roundId || index}
                          className="rounded-xl border border-amber-300 bg-amber-50 p-3"
                        >
                          <div className="flex justify-between gap-2">
                            <div className="font-bold text-sm text-amber-900">
                              {round.name}
                            </div>

                            <span className="text-[9px] px-2 py-1 rounded-full bg-amber-200 text-amber-900 font-bold">
                              ROUND
                            </span>
                          </div>

                          <div className="mt-3">
                            <div className="text-xl font-bold text-amber-800">
                              {formatTime12(round.startTime)}
                            </div>

                            <div className="text-xs text-amber-700">↓</div>

                            <div className="text-lg font-semibold text-amber-800">
                              {formatTime12(round.endTime)}
                            </div>
                          </div>

                          <div className="mt-3 flex flex-wrap gap-2">
                            <span className="text-[10px] px-2 py-1 rounded-lg bg-white border border-amber-200 text-amber-700">
                              ⏱ {formatDuration(round.startTime, round.endTime)}
                            </span>

                            <span className="text-[10px] px-2 py-1 rounded-lg bg-amber-100 text-amber-800">
                              🔒 BLOCKED
                            </span>
                          </div>

                          {canModifyDate && (
                            <button
                              onClick={() => handleDeleteRound(round)}
                              disabled={deleting}
                              className="w-full mt-3 px-3 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-bold disabled:opacity-50"
                            >
                              {deleting ? "Deleting..." : "Delete Round"}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </section>

            {/* OPERATION */}

            <section className="bg-white rounded-2xl border border-red-200 shadow-sm overflow-hidden">
              <div className="px-4 py-4 bg-red-50 border-b border-red-200">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <h2 className="font-bold text-red-900">
                      🏥 Operation Schedule
                    </h2>

                    <p className="text-[11px] text-red-700 mt-1">
                      Patient slots blocked
                    </p>
                  </div>

                  <span className="px-2 py-1 rounded-full bg-red-200 text-red-900 text-[10px] font-bold">
                    {normalizedOperations.length}
                  </span>
                </div>
              </div>

              <div className="p-3">
                {normalizedOperations.length === 0 ? (
                  <div className="text-center py-6 text-xs text-slate-400">
                    No operation schedule
                  </div>
                ) : (
                  <div className="space-y-3">
                    {normalizedOperations.map((operation, index) => {
                      const operationId = getOperationId(operation);

                      const deleting =
                        deletingAction === `operation-${operationId}`;

                      return (
                        <div
                          key={operationId || index}
                          className="rounded-xl border border-red-300 bg-red-50 p-3"
                        >
                          <div className="flex justify-between gap-2">
                            <div className="font-bold text-sm text-red-900">
                              {operation.name}
                            </div>

                            <span className="text-[9px] px-2 py-1 rounded-full bg-red-200 text-red-900 font-bold">
                              OPERATION
                            </span>
                          </div>

                          <div className="mt-3">
                            <div className="text-xl font-bold text-red-800">
                              {formatTime12(operation.startTime)}
                            </div>

                            <div className="text-xs text-red-700">↓</div>

                            <div className="text-lg font-semibold text-red-800">
                              {formatTime12(operation.endTime)}
                            </div>
                          </div>

                          <div className="mt-3 flex flex-wrap gap-2">
                            <span className="text-[10px] px-2 py-1 rounded-lg bg-white border border-red-200 text-red-700">
                              ⏱{" "}
                              {formatDuration(
                                operation.startTime,
                                operation.endTime,
                              )}
                            </span>

                            <span className="text-[10px] px-2 py-1 rounded-lg bg-red-100 text-red-800">
                              🔒 BLOCKED
                            </span>
                          </div>

                          {canModifyDate && (
                            <button
                              onClick={() => handleDeleteOperation(operation)}
                              disabled={deleting}
                              className="w-full mt-3 px-3 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-bold disabled:opacity-50"
                            >
                              {deleting ? "Deleting..." : "Delete Operation"}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </section>
          </div>

          {/* ==================================================
              COLUMN 3 — PATIENT SLOTS
          ================================================== */}

          <section className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden min-w-0">
            {/* HEADER */}

            <div
              className="sticky top-[73px] z-30 px-5 py-4 text-white shadow-sm"
              style={{
                background: CUSTOM_GRADIENT,
              }}
            >
              <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3">
                <div>
                  <h2 className="text-lg font-bold">
                    🩺 Patient Slot Management
                  </h2>

                  <p className="text-xs text-slate-300 mt-1">
                    Patient slots are blocked during rounds and operations
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setShowFreeOnly(!showFreeOnly)}
                    className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-xs font-semibold"
                  >
                    {showFreeOnly ? "Show All" : "Free Only"}
                  </button>

                  <button
                    onClick={() => {
                      if (!canModifyDate) {
                        alert(
                          selectedDateIsPast
                            ? "Past dates are view-only."
                            : "Holiday dates cannot be modified.",
                        );
                        return;
                      }

                      setShowAddSlot(!showAddSlot);
                    }}
                    disabled={!canModifyDate}
                    className="px-3 py-2 rounded-lg bg-white text-slate-800 text-xs font-bold disabled:opacity-40"
                  >
                    + Patient Slot
                  </button>

                  <button
                    onClick={handleAddDaySlots}
                    disabled={addingDay || !canModifyDate}
                    className="px-3 py-2 rounded-lg bg-emerald-500 text-white text-xs font-bold disabled:opacity-40"
                  >
                    {addingDay ? "Adding..." : "+ Add Day"}
                  </button>

                  <button
                    onClick={handleDeleteDay}
                    disabled={!canModifyDate || deletingAction === "day"}
                    className="px-3 py-2 rounded-lg bg-red-500 text-white text-xs font-bold disabled:opacity-40"
                  >
                    {deletingAction === "day" ? "Deleting..." : "Delete Day"}
                  </button>
                </div>
              </div>
            </div>

            {/* CONTENT */}

            <div className="max-h-[calc(100vh-130px)] overflow-y-auto p-5">
              {/* TODAY */}

              {selectedDateIsToday && (
                <div className="mb-4 px-4 py-3 rounded-xl border border-cyan-200 bg-cyan-50">
                  <div className="font-bold text-cyan-900 text-sm">
                    📅 Today
                  </div>

                  <div className="text-xs text-cyan-700 mt-1">
                    Passed times are locked and cannot be modified.
                  </div>
                </div>
              )}

              {/* PAST */}

              {selectedDateIsPast && (
                <div className="mb-4 p-4 rounded-xl border border-slate-300 bg-slate-100">
                  <div className="font-bold text-slate-800 text-sm">
                    🔒 Past Date — View Only
                  </div>

                  <div className="text-xs text-slate-600 mt-1">
                    Nothing on this date can be changed.
                  </div>
                </div>
              )}

              {/* ROUND/OPERATION CONFLICT */}

              {normalizedRounds.some((round) =>
                normalizedOperations.some((operation) =>
                  timesOverlap(
                    round.startTime,
                    round.endTime,
                    operation.startTime,
                    operation.endTime,
                  ),
                ),
              ) && (
                <div className="mb-4 p-4 rounded-xl border border-red-300 bg-red-50">
                  <div className="font-bold text-red-800 text-sm">
                    ⚠️ Round / Operation Conflict
                  </div>

                  <div className="text-xs text-red-700 mt-1">
                    A round and operation overlap. Patient slots remain blocked.
                  </div>
                </div>
              )}

              {/* ADD SLOT */}

              {showAddSlot && canModifyDate && (
                <div
                  className="mb-5 rounded-2xl p-5 text-white"
                  style={{
                    background: CUSTOM_GRADIENT,
                  }}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="font-bold">Add Patient Slot</h3>

                      <p className="text-[11px] text-slate-300 mt-1">
                        Session → Round → Operation → Patient validation
                      </p>
                    </div>

                    <button
                      onClick={() => setShowAddSlot(false)}
                      className="text-slate-300 hover:text-white"
                    >
                      ✕
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                    <select
                      value={newSlot.sessionName}
                      onChange={(e) =>
                        setNewSlot({
                          ...newSlot,
                          sessionName: e.target.value,
                          startTime: "",
                          endTime: "",
                        })
                      }
                      className="rounded-lg p-2 text-slate-800 text-sm"
                    >
                      {DEFAULT_SESSIONS.map((session) => (
                        <option key={session.name} value={session.name}>
                          {session.name} ({session.start} -{session.end})
                        </option>
                      ))}
                    </select>

                    <input
                      type="time"
                      min={
                        DEFAULT_SESSIONS.find(
                          (item) => item.name === newSlot.sessionName,
                        )?.start
                      }
                      max={
                        DEFAULT_SESSIONS.find(
                          (item) => item.name === newSlot.sessionName,
                        )?.end
                      }
                      value={newSlot.startTime}
                      onChange={(e) =>
                        setNewSlot({
                          ...newSlot,
                          startTime: e.target.value,
                        })
                      }
                      className="rounded-lg p-2 text-slate-800 text-sm"
                    />

                    <input
                      type="time"
                      min={
                        DEFAULT_SESSIONS.find(
                          (item) => item.name === newSlot.sessionName,
                        )?.start
                      }
                      max={
                        DEFAULT_SESSIONS.find(
                          (item) => item.name === newSlot.sessionName,
                        )?.end
                      }
                      value={newSlot.endTime}
                      onChange={(e) =>
                        setNewSlot({
                          ...newSlot,
                          endTime: e.target.value,
                        })
                      }
                      className="rounded-lg p-2 text-slate-800 text-sm"
                    />
                  </div>

                  {newSlot.startTime && newSlot.endTime && (
                    <div className="mt-3">
                      {(() => {
                        const validation = validatePatientTime(
                          newSlot.sessionName,
                          newSlot.startTime,
                          newSlot.endTime,
                        );

                        return validation.valid ? (
                          <div className="text-xs text-emerald-300">
                            ✓ Time is available
                          </div>
                        ) : (
                          <div className="text-xs text-red-300">
                            ⚠️ {validation.message}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  <button
                    onClick={handleAddSlot}
                    disabled={addingSlot}
                    className="mt-4 px-5 py-2 rounded-lg bg-white text-slate-800 text-sm font-bold disabled:opacity-50"
                  >
                    {addingSlot ? "Adding..." : "Add Patient Slot"}
                  </button>
                </div>
              )}

              {/* STATS */}

              <div className="grid grid-cols-2 xl:grid-cols-5 gap-3 mb-5">
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                  <div className="text-[10px] text-emerald-700">AVAILABLE</div>

                  <div className="text-2xl font-bold text-emerald-700">
                    {stats.free}
                  </div>
                </div>

                <div className="rounded-xl border border-red-200 bg-red-50 p-3">
                  <div className="text-[10px] text-red-700">BOOKED</div>

                  <div className="text-2xl font-bold text-red-700">
                    {stats.booked}
                  </div>
                </div>

                <div className="rounded-xl border border-orange-200 bg-orange-50 p-3">
                  <div className="text-[10px] text-orange-700">BLOCKED</div>

                  <div className="text-2xl font-bold text-orange-700">
                    {stats.blocked}
                  </div>
                </div>

                <div className="rounded-xl border border-slate-300 bg-slate-100 p-3">
                  <div className="text-[10px] text-slate-600">PAST</div>

                  <div className="text-2xl font-bold text-slate-700">
                    {stats.past}
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="text-[10px] text-slate-500">TOTAL</div>

                  <div className="text-2xl font-bold text-slate-800">
                    {stats.total}
                  </div>
                </div>
              </div>

              {/* LOADING */}

              {loading && (
                <div className="text-center py-4 text-sm text-sky-600">
                  Loading availability...
                </div>
              )}

              {/* NO SLOTS */}

              {displayedSessions.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-5xl">📅</div>

                  <div className="font-bold text-slate-700 mt-3">
                    No patient slots available
                  </div>

                  {!selectedDateIsPast && !isHoliday && (
                    <button
                      onClick={handleAddDaySlots}
                      disabled={addingDay}
                      className="mt-4 px-5 py-2 rounded-lg text-white text-sm font-semibold"
                      style={{
                        background: BUTTON_GRADIENT,
                      }}
                    >
                      {addingDay ? "Adding..." : "Add Day Availability"}
                    </button>
                  )}
                </div>
              ) : (
                /* SESSIONS */

                <div className="space-y-5">
                  {displayedSessions.map((session, sessionIndex) => {
                    const sessionKey =
                      session._id || session.id || session.name || sessionIndex;

                    const sessionBooked = (session.slots || []).some(
                      (slot) => slot.isBooked,
                    );

                    const sessionDeleting =
                      deletingAction === `session-${session.name}`;

                    return (
                      <div
                        key={sessionKey}
                        className="rounded-xl border border-slate-200 overflow-hidden"
                      >
                        {/* SESSION HEADER */}

                        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div>
                              <h3 className="font-bold text-slate-800">
                                🕐 {session.name}
                              </h3>

                              {SESSION_RANGES[session.name] && (
                                <div className="text-[10px] text-slate-500 mt-1">
                                  {minutesToTime(
                                    SESSION_RANGES[session.name].start,
                                  )}{" "}
                                  -{" "}
                                  {minutesToTime(
                                    SESSION_RANGES[session.name].end,
                                  )}
                                </div>
                              )}
                            </div>

                            <div className="flex items-center gap-2">
                              <div className="text-xs text-slate-500">
                                {session.slots?.length || 0} slots
                              </div>

                              {canModifyDate && (
                                <button
                                  onClick={() => handleDeleteSession(session)}
                                  disabled={sessionDeleting}
                                  className="px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-[10px] font-bold disabled:opacity-50"
                                >
                                  {sessionDeleting
                                    ? "Deleting..."
                                    : "Delete Session"}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* SLOTS */}

                        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                          {(session.slots || []).map((slot, slotIndex) => {
                            const slotId = getSlotId(slot);

                            const slotPast = isSlotPast(slot);

                            const blocker = findBlockingSchedule(
                              slot.startTime,
                              slot.endTime,
                            );

                            const isEditing = editingSlotId === slotId;

                            const locked =
                              selectedDateIsPast ||
                              isHoliday ||
                              slotPast ||
                              slot.isBooked ||
                              !!blocker ||
                              slot.isActive === false;

                            const deleting =
                              deletingAction === `slot-${slotId}`;

                            return (
                              <div
                                key={slotId || slotIndex}
                                className={`rounded-xl border p-4 ${
                                  slot.isBooked
                                    ? "bg-red-50 border-red-300"
                                    : blocker
                                      ? "bg-orange-50 border-orange-300"
                                      : slotPast
                                        ? "bg-slate-100 border-slate-300"
                                        : "bg-emerald-50 border-emerald-300"
                                }`}
                              >
                                {/* TIME */}

                                <div className="flex justify-between items-start gap-2">
                                  <div>
                                    <div className="font-bold text-slate-800">
                                      {formatTime12(slot.startTime)}
                                    </div>

                                    <div className="text-xs text-slate-500">
                                      to {formatTime12(slot.endTime)}
                                    </div>
                                  </div>

                                  {slot.isBooked ? (
                                    <span className="text-[9px] px-2 py-1 rounded-full bg-red-600 text-white font-bold">
                                      BOOKED
                                    </span>
                                  ) : blocker ? (
                                    <span className="text-[9px] px-2 py-1 rounded-full bg-orange-600 text-white font-bold">
                                      BLOCKED
                                    </span>
                                  ) : slotPast ? (
                                    <span className="text-[9px] px-2 py-1 rounded-full bg-slate-500 text-white font-bold">
                                      PAST
                                    </span>
                                  ) : slot.isActive === false ? (
                                    <span className="text-[9px] px-2 py-1 rounded-full bg-slate-500 text-white font-bold">
                                      INACTIVE
                                    </span>
                                  ) : (
                                    <span className="text-[9px] px-2 py-1 rounded-full bg-emerald-600 text-white font-bold">
                                      FREE
                                    </span>
                                  )}
                                </div>

                                {/* BLOCKER */}

                                {blocker && (
                                  <div className="mt-3 p-2 rounded-lg bg-white/70 border border-orange-200">
                                    <div className="text-[10px] font-bold text-orange-800">
                                      {blocker.type === "round"
                                        ? "👨‍⚕️ Doctor Round"
                                        : "🏥 Operation"}
                                    </div>

                                    <div className="text-[10px] text-orange-700 mt-1">
                                      {blocker.name}
                                    </div>

                                    <div className="text-[10px] text-orange-700">
                                      {formatTime12(blocker.startTime)} -{" "}
                                      {formatTime12(blocker.endTime)}
                                    </div>
                                  </div>
                                )}

                                {/* EDIT */}

                                {isEditing ? (
                                  <div className="mt-3 space-y-2">
                                    <input
                                      type="time"
                                      value={editValues.startTime}
                                      onChange={(e) =>
                                        setEditValues({
                                          ...editValues,
                                          startTime: e.target.value,
                                        })
                                      }
                                      className="w-full border rounded-lg px-2 py-2 text-sm"
                                    />

                                    <input
                                      type="time"
                                      value={editValues.endTime}
                                      onChange={(e) =>
                                        setEditValues({
                                          ...editValues,
                                          endTime: e.target.value,
                                        })
                                      }
                                      className="w-full border rounded-lg px-2 py-2 text-sm"
                                    />

                                    {editValues.startTime &&
                                      editValues.endTime && (
                                        <div className="text-[10px]">
                                          {(() => {
                                            const validation =
                                              validatePatientTime(
                                                session.name,
                                                editValues.startTime,
                                                editValues.endTime,
                                                slotId,
                                              );

                                            return validation.valid ? (
                                              <span className="text-emerald-700">
                                                ✓ Time available
                                              </span>
                                            ) : (
                                              <span className="text-red-700">
                                                ⚠️ {validation.message}
                                              </span>
                                            );
                                          })()}
                                        </div>
                                      )}

                                    <div className="flex gap-2">
                                      <button
                                        onClick={() =>
                                          handleUpdateSlot({
                                            ...slot,
                                            sessionName: session.name,
                                          })
                                        }
                                        className="flex-1 px-2 py-2 rounded-lg bg-emerald-600 text-white text-xs font-bold"
                                      >
                                        Save
                                      </button>

                                      <button
                                        onClick={() => {
                                          setEditingSlotId(null);

                                          setEditValues({
                                            startTime: "",
                                            endTime: "",
                                          });
                                        }}
                                        className="flex-1 px-2 py-2 rounded-lg bg-slate-500 text-white text-xs"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="mt-3">
                                    <div className="text-[10px] text-slate-500">
                                      Duration:{" "}
                                      {formatDuration(
                                        slot.startTime,
                                        slot.endTime,
                                      )}
                                    </div>

                                    {!locked && (
                                      <div className="flex gap-2 mt-3">
                                        <button
                                          onClick={() => {
                                            setEditingSlotId(slotId);

                                            setEditValues({
                                              startTime: slot.startTime,
                                              endTime: slot.endTime,
                                            });
                                          }}
                                          className="flex-1 px-2 py-2 rounded-lg text-white text-xs font-bold"
                                          style={{
                                            background: BUTTON_GRADIENT,
                                          }}
                                        >
                                          Edit
                                        </button>

                                        <button
                                          onClick={() => handleDeleteSlot(slot)}
                                          disabled={deleting}
                                          className="px-3 py-2 rounded-lg bg-red-600 text-white text-xs disabled:opacity-50"
                                        >
                                          {deleting ? "..." : "Delete"}
                                        </button>
                                      </div>
                                    )}

                                    {selectedDateIsPast && (
                                      <div className="mt-3 text-[10px] text-slate-600">
                                        🔒 Past date — locked
                                      </div>
                                    )}

                                    {slotPast && !selectedDateIsPast && (
                                      <div className="mt-3 text-[10px] text-slate-600">
                                        🔒 Time already passed
                                      </div>
                                    )}

                                    {slot.isBooked && (
                                      <div className="mt-3 text-[10px] text-red-700">
                                        🔒 Booked slot
                                      </div>
                                    )}

                                    {blocker && (
                                      <div className="mt-3 text-[10px] text-orange-700">
                                        🔒 Blocked by{" "}
                                        {blocker.type === "round"
                                          ? "doctor round"
                                          : "operation"}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      {/* ======================================================
          ERROR
      ====================================================== */}

      {error && (
        <div className="fixed bottom-5 right-5 z-[100] max-w-sm bg-red-600 text-white px-5 py-3 rounded-xl shadow-xl">
          ❌{" "}
          {typeof error === "string"
            ? error
            : error?.message || "Something went wrong"}
        </div>
      )}

      {/* ======================================================
          SUCCESS
      ====================================================== */}

      {success && (
        <div className="fixed bottom-5 right-5 z-[100] max-w-sm bg-emerald-600 text-white px-5 py-3 rounded-xl shadow-xl">
          ✓ {success}
        </div>
      )}
    </div>
  );
};

export default DoctorSlotManager;
